import { IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonPage, IonText, IonTitle, IonToolbar } from '@ionic/react';
import { close } from 'ionicons/icons';
import { CapacitorCrisp } from '@capgo/capacitor-crisp';
import { useEffect, useRef } from 'react';
import { useModalTranslation } from '../hooks/useTranslation';
import { UserExperienceRatingActionTaken, useSetUserExperienceRatingPromptResultMutation } from '../query/userExperienceRatingPromptQuery';
import styles from './RateExperienceFollowupModal.module.css';

const GOOGLE_REVIEW_URL = 'https://g.page/r/Ce9YVOdRqzECEAE/review';
const TRUSTPILOT_REVIEW_URL = 'https://uk.trustpilot.com/review/onlycat.com';
const GOOGLE_ICON_SRC = '/assets/icons/Google_%22G%22_logo.svg';
const TRUSTPILOT_ICON_SRC = '/assets/icons/trustpilot-2.svg';

type RateExperienceFollowupModalProps = {
    dismiss: (data?: string | null | undefined | number, role?: string) => void;
    rating: number;
    promptId?: number;
    onComplete?: () => void;
};

const RateExperienceFollowupModal: React.FC<RateExperienceFollowupModalProps> = ({ dismiss, rating, promptId, onComplete }) => {
    const { t } = useModalTranslation();
    const isPositive = rating >= 4;
    const setPromptResultMutation = useSetUserExperienceRatingPromptResultMutation();
    const promptIdRef = useRef<number | null>(promptId ?? null);

    useEffect(() => {
        if (promptId) {
            promptIdRef.current = promptId;
        }
    }, [promptId]);

    const recordAction = (actionTaken: UserExperienceRatingActionTaken) => {
        if (!promptIdRef.current) {
            return;
        }

        setPromptResultMutation.mutate({ promptId: promptIdRef.current, actionTaken });
    };

    const handleDismiss = () => {
        recordAction("dismissed");
        dismiss(null, "cancel");
        onComplete?.();
    };

    const openExternalLink = (url: string, actionTaken: UserExperienceRatingActionTaken) => {
        recordAction(actionTaken);
        window.open(url, '_blank', 'noopener,noreferrer');
        dismiss(null, 'action');
        onComplete?.();
    };

    const openSupport = async () => {
        recordAction("contact_support");
        await CapacitorCrisp.openMessenger();
        dismiss(null, 'action');
        onComplete?.();
    };

    const renderBody = (body: string) => (
        <IonText className={styles.body}>
            {body
                .split('\n')
                .map((line) => line.trim())
                .filter(Boolean)
                .map((line, index) => (
                    <p key={`${line}-${index}`}>{line}</p>
                ))}
        </IonText>
    );

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonTitle className={styles.title}>
                        {isPositive
                            ? t('rate_experience_followup.positive.title')
                            : t('rate_experience_followup.negative.title')}
                    </IonTitle>
                    <IonButtons slot="end">
                        <IonButton onClick={handleDismiss}>
                            <IonIcon slot="icon-only" icon={close} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen className={styles.content}>
                {isPositive
                    ? renderBody(t('rate_experience_followup.positive.body'))
                    : renderBody(t('rate_experience_followup.negative.body'))}

                <div className={styles.actions}>
                    {isPositive ? (
                        <>
                            <IonButton expand="block" fill="outline" onClick={() => openExternalLink(TRUSTPILOT_REVIEW_URL, "trustpilot_review")}>
                                <IonIcon slot="start" src={TRUSTPILOT_ICON_SRC} />
                                {t('rate_experience_followup.positive.trustpilot_button')}
                            </IonButton>
                            <IonButton expand="block" fill="outline" onClick={() => openExternalLink(GOOGLE_REVIEW_URL, "google_review")}>
                                <IonIcon slot="start" src={GOOGLE_ICON_SRC} />
                                {t('rate_experience_followup.positive.google_button')}
                            </IonButton>
                            <IonButton expand="block" fill="clear" onClick={handleDismiss}>
                                {t('rate_experience_followup.positive.secondary_maybe_later')}
                            </IonButton>
                            <IonButton expand="block" fill="clear" onClick={handleDismiss}>
                                {t('rate_experience_followup.positive.secondary_already')}
                            </IonButton>
                        </>
                    ) : (
                        <>
                            <IonButton expand="block" onClick={openSupport}>
                                {t('rate_experience_followup.negative.contact_support')}
                            </IonButton>
                            <IonButton expand="block" fill="clear" onClick={handleDismiss}>
                                {t('rate_experience_followup.negative.secondary_maybe_later')}
                            </IonButton>
                        </>
                    )}
                </div>
            </IonContent>
        </IonPage>
    );
};

export default RateExperienceFollowupModal;
